import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  ColumnDef,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";

export interface ReportTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  pageSize?: number;
  showPagination?: boolean;
  className?: string;
  emptyMessage?: string;
}

export function ReportTable<TData>({
  data,
  columns,
  pageSize = 10,
  showPagination = true,
  className,
  emptyMessage = "Nenhum dado disponível.",
}: ReportTableProps<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: showPagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  return (
    <div className={cn("space-y-4", className)}>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : (
                      <div
                        className={cn(
                          header.column.getCanSort() &&
                          "flex items-center space-x-2 cursor-pointer select-none hover:text-foreground"
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <ArrowUpDown className="ml-2 h-4 w-4" />
                        )}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {showPagination && data.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            Página {table.getState().pagination.pageIndex + 1} de{" "}
            {table.getPageCount()} ({data.length} registros)
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">Linhas por página</p>
              <Select
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={(value) => {
                  table.setPageSize(Number(value));
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={table.getState().pagination.pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[5, 10, 20, 30, 50].map((pageSize) => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                aria-label="Primeira página"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                aria-label="Próxima página"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                aria-label="Última página"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Check if a value is a Date object or a valid date string that needs formatting
 */
function isDate(value: any): boolean {
  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }
  if (typeof value === "string") {
    // Check if it looks like a date string (contains GMT, day names, etc.)
    const looksLikeDate = /GMT|UTC|Standard Time|Day|Mon|Tue|Wed|Thu|Fri|Sat|Sun/i.test(value);
    if (looksLikeDate) {
      const date = new Date(value);
      return !isNaN(date.getTime());
    }
    // Check if it's a valid ISO date string
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}/;
    if (isoDatePattern.test(value)) {
      const date = new Date(value);
      return !isNaN(date.getTime());
    }
  }
  return false;
}

/**
 * Format a date value to a friendly Brazilian format
 */
function formatDateValue(value: any, formatString: string = "dd/MM/yyyy"): string {
  if (!value) return "";

  // If it's already a formatted date string (dd/MM/yyyy pattern), return as-is
  if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}/.test(value)) {
    return value;
  }

  try {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return formatDate(date, formatString);
  } catch {
    return String(value);
  }
}

export interface CreateSortableColumnOptions<TData = any> {
  /**
   * Format string for date columns (default: "dd/MM/yyyy")
   * If provided, the column will be treated as a date column
   */
  dateFormat?: string;
  /**
   * Custom cell renderer - uses TanStack Table's standard signature
   */
  cell?: (context: { row: { original: TData }; getValue: () => any }) => React.ReactNode;
}

/**
 * Create a sortable column definition
 * Automatically detects and formats dates
 */
export function createSortableColumn<TData>(
  accessorKey: string,
  header: string,
  options?: CreateSortableColumnOptions<TData>
): ColumnDef<TData> {
  const { dateFormat, cell } = options || {};

  return {
    accessorKey,
    header,
    enableSorting: true,
    cell: (context) => {
      const { row, getValue } = context;
      const value = getValue();

      // Use custom cell renderer if provided
      if (cell) {
        return cell(context);
      }

      // Auto-detect and format dates
      if (value && (dateFormat || isDate(value))) {
        return formatDateValue(value, dateFormat);
      }

      // Return value as-is for non-date values
      return value ?? "";
    },
  };
}

/**
 * Create a date column with Brazilian format
 */
export function createDateColumn<TData>(
  accessorKey: string,
  header: string,
  formatString: string = "dd/MM/yyyy"
): ColumnDef<TData> {
  return createSortableColumn<TData>(accessorKey, header, {
    dateFormat: formatString,
  });
}
